import { TypeChecker } from '@attain-apps/type-guards'

import type { Expression } from '../expression'

import { type AllFn, getAllHelpers, getHelper } from './all'
import { type Alias, Key } from './key'

/**
 * Represents a function call in a DMS {@link Expression}
 */
// NOTE: we want to be able to index `Fn` with `Key` or `Alias`
export type Fn<T = any> = Partial<Fn.UsingKey<T> & Fn.UsingAlias<T>>
export namespace Fn {
  export const isFn: TypeChecker<Fn> = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.union(UsingKey.isUsingKey, UsingAlias.isUsingAlias),
  ).usingAlias('Fn')

  /**
   * Retrieves all {@link Expression} used within the args of the given
   * {@link Fn}
   */
  export function expressionsInArgs(fn: Fn): readonly Expression[] {
    return getHelper(fn).expressionsInArgs(fn)
  }

  // NOTE: `ExactlyOneOf` would be more correct, but it slows TypeScript down a
  // lot
  export type UsingKey<T = any> = Partial<AllFn<T>>
  export namespace UsingKey {
    export const isUsingKey: TypeChecker<UsingKey> = TypeChecker.Factory.lazy(
      () =>
        TypeChecker.Factory.union(
          ...getAllHelpers().map(helper => helper.typeChecker),
        ),
    ).usingAlias('Fn.UsingKey')

    export type From<TFn extends Fn> = TFn extends UsingKey
      ? TFn
      : Record<
          Key.From<Extract<keyof TFn, Alias>>,
          AllFn[Key.From<Extract<keyof TFn, Alias>>]
        >

    /**
     * Standardizes a {@link Fn} into a {@link Fn.UsingKey} so that it can be
     * effectively processed
     */
    export function from<TFn extends Fn>(fn: TFn): From<TFn>
    export function from(fn: Fn): UsingKey {
      fn = { ...fn }
      for (const keyOrAlias in fn) {
        if (!Key.isKey(keyOrAlias)) {
          const key = Key.from(keyOrAlias)
          if (key) {
            // NOTE: lots of casting here because the types are too complex for
            // TypeScript, leading to very large unions
            ;(fn as any)[key] = fn[keyOrAlias as keyof typeof fn]
            delete fn[keyOrAlias as keyof typeof fn]
          }
        }
      }
      // NOTE: the logic above should make this true
      return fn as UsingKey
    }
  }

  // NOTE: `ExactlyOneOf` would be more correct, but it slows TypeScript down a
  // lot
  export type UsingAlias<T = any> = Partial<{
    [TAlias in Alias]: AllFn<T>[Key.From<TAlias>]
  }>
  export namespace UsingAlias {
    export const isUsingAlias: TypeChecker<UsingAlias> =
      TypeChecker.Factory.custom(
        (val): val is UsingAlias => {
          if (!TypeChecker.isObject(val)) {
            return false
          }
          // NOTE: this is a bit of a hack, but the idea is that a performant way to
          // test the value is just to swap any aliases with keys and then see if
          // that results in a valid fn
          return UsingKey.isUsingKey(UsingKey.from(val as UsingAlias))
        },
        {
          typeDescription: 'Fn.UsingAlias',
        },
      )
  }
}
